import type { HistoryDisplayEntry, HistoryDisplayModel } from './types';

const HISTORY_VALUE_LABELS: Record<string, string> = {
  пусто: 'Пусто',
  красный: 'Красный',
  синий: 'Синий',
  оранжевый: 'Оранжевый',
  зеленый: 'Зеленый',
  зелёный: 'Зелёный',
  желтый: 'Желтый',
  жёлтый: 'Жёлтый',
  фиолетовый: 'Фиолетовый',
  серый: 'Серый',
  белый: 'Белый',
  черный: 'Черный',
  чёрный: 'Чёрный',
};

function formatHistoryValue(value: string | null | undefined): string {
  const raw = value ?? 'пусто';
  const normalized = raw.trim().toLowerCase();
  return HISTORY_VALUE_LABELS[normalized] ?? raw;
}

export function mapHistoryEntryToDisplay(entry: HistoryDisplayEntry): HistoryDisplayModel {
  const { segments } = entry;
  if (segments.length === 0) {
    return { type: 'plain', text: entry.field_path ?? entry.change_type };
  }

  const fieldLabel = segments[0]!.field_label;

  if (segments.length === 1) {
    const s = segments[0]!;
    if (s.kind === 'project_created') {
      return {
        type: 'project_action',
        action: 'created',
        name: s.after ?? '',
      };
    }
    if (s.kind === 'project_deleted') {
      return {
        type: 'project_action',
        action: 'deleted',
        name: s.before ?? '',
      };
    }
    if (s.kind === 'project_restored') {
      return {
        type: 'project_action',
        action: 'restored',
        name: s.after ?? '',
      };
    }
    if (s.kind === 'session_created') {
      return {
        type: 'session_action',
        action: 'created',
        lifecycle: s.session_lifecycle!,
      };
    }
    if (s.kind === 'session_deleted') {
      return {
        type: 'session_action',
        action: 'deleted',
        lifecycle: s.session_lifecycle!,
      };
    }
    if (s.kind === 'session_restored') {
      return {
        type: 'session_action',
        action: 'restored',
        lifecycle: s.session_lifecycle!,
      };
    }
    if (s.kind === 'scalar_diff') {
      return {
        type: 'field_change',
        fieldLabel,
        before: formatHistoryValue(s.before),
        after: formatHistoryValue(s.after),
      };
    }
    if (s.kind === 'list_before_after') {
      return {
        type: 'field_change',
        fieldLabel,
        before: formatHistoryValue(s.before),
        after: formatHistoryValue(s.after),
      };
    }
    if (s.kind === 'order_only' && s.message) {
      return {
        type: 'relation_change',
        fieldLabel,
        entries: [{ kind: 'order_only', message: s.message }],
      };
    }
    if (s.kind === 'relation_attr_diff') {
      return {
        type: 'relation_change',
        fieldLabel,
        entries: [
          {
            kind: 'attribute_diff',
            entityName: s.entity_name ?? '',
            attribute: s.attribute ?? '',
            before: formatHistoryValue(s.before),
            after: formatHistoryValue(s.after),
          },
        ],
      };
    }
  }

  const relationEntries: Extract<
    HistoryDisplayModel,
    { type: 'relation_change' }
  >['entries'] = [];

  for (const s of segments) {
    if (s.kind === 'relation_attr_diff') {
      relationEntries.push({
        kind: 'attribute_diff',
        entityName: s.entity_name ?? '',
        attribute: s.attribute ?? '',
        before: formatHistoryValue(s.before),
        after: formatHistoryValue(s.after),
      });
    } else if (s.kind === 'list_before_after') {
      relationEntries.push({
        kind: 'list_before_after',
        before: formatHistoryValue(s.before),
        after: formatHistoryValue(s.after),
      });
    } else if (s.kind === 'order_only' && s.message) {
      relationEntries.push({ kind: 'order_only', message: s.message });
    } else if (s.kind === 'scalar_diff') {
      return {
        type: 'field_change',
        fieldLabel: s.field_label,
        before: formatHistoryValue(s.before),
        after: formatHistoryValue(s.after),
      };
    }
  }

  if (relationEntries.length > 0) {
    return { type: 'relation_change', fieldLabel, entries: relationEntries };
  }

  return { type: 'plain', text: `${fieldLabel}` };
}
